import { api } from "../../api/client";
import type {
  DeclinationItem,
  DeclinationNotice,
  DeclinationReceived,
  DeclinationState,
  GroupedDocument,
  GroupedNode,
  Level,
} from "../../api/types";
import { k, t, tServer } from "../../i18n/i18n";
import { missionName, unitMissionName } from "../../i18n/vocabulary";
import { DECLINABLE } from "../../echelons";

/** Déclinaison de l'ordre d'un dossier de GTIA, de brigade, de division ou de corps d'armée aux subordonnés (docs/20 §13, docs/21) : textes des actions
 *  selon le niveau, avis traduits, action attendue, sections du document regroupé. Fonctions pures, testées par
 *  declination.test.ts. */

/** Cible désignée (`data-attention`) du bouton qui lance, reprend ou met à jour la génération. */
export const GENERATE_TARGET = "declination-generate";

/** Niveaux d'où l'ordre se décline : brigade (→ GTIA), division (→ brigades, puis GTIA), corps d'armée (→ divisions, puis
 *  brigades, docs/08 §24), GTIA (→ S/GTIA, niveau compagnie, docs/21) ; table des échelons (registre B47). */
export { DECLINABLE };

export function declinable(level: Level | null | undefined): boolean {
  return Boolean(level && DECLINABLE.includes(level));
}

/** Après l'import d'un OPO (décision du 06/10/2026 : « pour l'import seulement, les ordres subordonnés sont générés
 *  directement ») : un dossier de brigade ou de division dont le terrain est prêt s'ouvre sur ses ordres subordonnés,
 *  la génération désignée comme action attendue. */
export function offerAfterImport(level: Level | null | undefined, mapSlug: string | null, terrainReady: boolean): boolean {
  return declinable(level) && Boolean(mapSlug) && terrainReady;
}

/** Terrain libre encore en préparation après l'import (cas courant d'un terrain recréé) : la génération est proposée
 *  par l'avis « Terrain prêt » (`useZoneWatch`), l'intention retenue pour ce dossier. */
export function offerAfterTerrain(level: Level | null | undefined, mapSlug: string | null, terrainReady: boolean): boolean {
  return declinable(level) && Boolean(mapSlug) && !terrainReady;
}

const LATER_KEY = "generex-decline-after-terrain";
const later = new Set<string>();

function readLater(): Set<string> {
  try {
    const raw = sessionStorage.getItem(LATER_KEY);
    if (raw) for (const id of JSON.parse(raw) as string[]) later.add(id);
  } catch {
    // stockage indisponible (navigation privée, aperçu) : l'intention reste en mémoire
  }
  return later;
}

function writeLater() {
  try {
    sessionStorage.setItem(LATER_KEY, JSON.stringify([...later]));
  } catch {
    // stockage indisponible : l'intention reste en mémoire
  }
}

/** Retient qu'un dossier importé attend son terrain pour générer les ordres de ses subordonnés. */
export function declineAfterTerrain(exerciseId: string) {
  readLater().add(exerciseId);
  writeLater();
}

/** Le dossier attendait-il son terrain pour générer ses ordres subordonnés ? L'intention est consommée. */
export function takeDeclineAfterTerrain(exerciseId: string): boolean {
  const had = readLater().delete(exerciseId);
  if (had) writeLater();
  return had;
}

/** Action de lancement, de remise à jour ou de reprise, en clair selon le niveau du dossier. */
export function actionLabel(level: Level | null | undefined, kind: "generate" | "update" | "resume"): string {
  const division = level === "DIVISION";
  if (kind === "resume") return t("Reprendre la génération");
  if (level === "CORPS")
    return kind === "update"
      ? t("Mettre à jour les ordres des divisions et des brigades")
      : t("Générer les ordres des divisions et des brigades");
  if (level === "REGIMENT") return kind === "update" ? t("Mettre à jour les ordres des S/GTIA") : t("Générer les ordres des S/GTIA");
  if (kind === "update")
    return division ? t("Mettre à jour les ordres des brigades et des GTIA") : t("Mettre à jour les ordres des GTIA");
  return division ? t("Générer les ordres des brigades et des GTIA") : t("Générer les ordres des GTIA");
}

/** « 8 ordres à rédiger » (division : brigades et GTIA). */
export function plannedText(planned: number): string {
  return planned <= 1 ? t("{n} ordre à rédiger", { n: planned }) : t("{n} ordres à rédiger", { n: planned });
}

/** Nom d'un niveau subordonné, au singulier. */
export function levelNoun(level: Level | null | undefined): string {
  if (level === "BRIGADE") return t("brigade");
  if (level === "DIVISION") return t("division");
  if (level === "COMPAGNIE") return t("S/GTIA");
  return t("GTIA");
}

/** Badge du niveau d'un subordonné décliné : « GTIA » ; S/GTIA : « Compagnie » ou « Escadron » (décision 1,
 *  `unitLabel` publié par le serveur), « S/GTIA » à défaut ; sinon le nom du niveau. */
export function subordinateBadge(level: Level | null | undefined, unitLabel?: string | null): string | null {
  if (level === "REGIMENT") return t("GTIA");
  if (level === "COMPAGNIE") return unitLabel ? t(unitLabel) : t("S/GTIA");
  return null;
}

/** Noms des paragraphes de l'ordre que le cadre de l'échelon supérieur peut laisser à revoir. */
const FIELD_NAMES: Record<string, string> = {
  missionStatementText: k("mission"),
  maneuverConceptText: k("idée de manœuvre"),
  friendlyForcesText: k("forces amies"),
  limitsCoordination: k("limites"),
  coordinationLines: k("mesures de coordination"),
  sgtiaUnitMissions: k("missions des S/GTIA"),
  brigadeUnitMissions: k("missions des GTIA"),
  weatherText: k("météo"),
};

/** Noms des paragraphes, dans l'ordre d'un S/GTIA (`level` « COMPAGNIE ») : ses missions sont celles des sections ou
 *  des pelotons. */
export function fieldNames(fields: readonly string[] | undefined, level?: Level | null): string {
  const name = (f: string) =>
    level === "COMPAGNIE" && f === "sgtiaUnitMissions" ? t("missions des sections ou des pelotons") : t(FIELD_NAMES[f]!);
  const known = (fields ?? []).filter((f) => FIELD_NAMES[f]).map(name);
  // Paragraphes sans nom ici (appuis, effort…) : jamais de nom de champ interne à l'écran.
  if ((fields ?? []).some((f) => !FIELD_NAMES[f])) known.push(t("autres paragraphes"));
  return known.join(", ");
}

/** Avis à montrer pour un subordonné : celui d'une unité absente de l'ordre du chef dit déjà la mission à choisir et
 *  les paragraphes à revoir (un seul encadré, pas trois qui répètent la même chose). */
export function visibleNotices(notices: readonly DeclinationNotice[]): DeclinationNotice[] {
  const missing = notices.some((n) => n.code === "not_in_order");
  return notices.filter((n) => !missing || (n.code !== "mission_fallback" && n.code !== "frame_partial"));
}

/** Ton d'un avis : `warn` pour ce que l'utilisateur doit vérifier ou choisir, `info` pour le reste. */
export function noticeTone(code: string): "warn" | "info" {
  return ["mission_fallback", "corridor_refused", "corridor_missing", "corridor_camp", "corridor_slice", "corridor_clipped",
    "corridor_parent", "band_width", "not_in_order", "order_unavailable", "frame_partial", "parent_missing",
    "not_transmissible", "child_not_editable", "parent_edits_cited", "objective_outside", "mission_reserve", "eni_template", "map_kept",
    "removed"].includes(code)
    ? "warn"
    : "info";
}

/** Avis d'une déclinaison, en clair, dans la langue de l'interface (code inconnu : rien) ; `level` : niveau du
 *  subordonné (noms de ses paragraphes). */
export function noticeText(notice: DeclinationNotice, level?: Level | null): string {
  switch (notice.code) {
    case "parent_order_written":
      return t("L'OPO n° {n} du dossier n'était pas rédigé : il l'a été pour donner leurs ordres aux subordonnés.", { n: notice.number ?? 1 });
    case "mission_approximated":
      return t("Mission la plus proche de la tâche reçue « {task} » : {mission}. Vérifiez-la dans les Données initiales.", {
        task: unitMissionName(notice.task ?? ""),
        mission: missionName(notice.mission ?? ""),
      });
    case "mission_fallback":
      return notice.mission
        ? t("Aucune mission de ce niveau ne correspond aux tâches reçues : « {mission} » est posée, choisissez la mission dans les Données initiales.", { mission: missionName(notice.mission) })
        : t("Aucune mission de ce niveau ne correspond aux tâches reçues : choisissez la mission dans les Données initiales.");
    case "mission_kept":
      return t("Mission choisie dans le dossier gardée : {mission}.", { mission: missionName(notice.mission ?? "") });
    case "corridor_shifted":
      return t("Fuseau ramené vers l'axe de l'échelon supérieur pour tenir dans le terrain.");
    case "corridor_camp":
      return t("Fuseau de l'échelon supérieur plus large que le terrain : fuseau par défaut du terrain, aux GDH des LIMA de l'échelon supérieur. Limites, idée de manœuvre et missions des subordonnés à revoir.");
    case "corridor_slice":
      return t("Fuseau de l'échelon supérieur plus large que le terrain : fuseau tracé dans le terrain d'après la place de l'unité dans le dispositif, aux GDH des LIMA de l'échelon supérieur. Limites à vérifier.");
    case "corridor_clipped":
      return t("Fuseau réduit par l'emprise du camp : la bande de l'unité, jointive de celles des unités de son échelon, est rognée aux limites du terrain et peut être plus étroite que la largeur prévue par la doctrine. Limites à vérifier.");
    case "corridor_parent":
      return t("Aucune bande du fuseau de l'échelon supérieur n'est assez large pour cette unité sur le terrain libre : elle reçoit le fuseau entier de son chef, aux GDH de ses LIMA. Limites à reprendre.");
    case "band_width":
      return t("Bande de l'unité dans le fuseau de son chef nettement hors de la largeur prévue par la doctrine pour son niveau (fuseaux juxtaposés d'un fuseau très large) : limites à vérifier.");
    case "dossier_reused":
      return t("Dossier que cette unité avait déjà reçu de son chef : repris et mis à jour, plutôt qu'un second dossier.");
    case "parent_edits_cited":
      return t("Paragraphes retouchés à la main dans l'ordre du chef ({fields}) : cités tels qu'il les a écrits, dans leur langue. À revoir chez les subordonnés.", { fields: fieldNames(notice.fields, level) });
    case "corridor_refused":
      return t("Aucune bande du fuseau de l'échelon supérieur ne tient dans le terrain : fuseau par défaut, à vérifier.");
    case "corridor_missing":
      return t("Fuseau de l'échelon supérieur introuvable : fuseau par défaut, à vérifier.");
    case "corridor_kept":
      return t("Fuseau retouché dans le dossier gardé.");
    case "not_in_order":
      return t("L'ordre de l'échelon supérieur ne donne pas de mission à cette unité : choisissez sa mission dans ses Données initiales, puis complétez son ordre (paragraphes marqués « À revoir »).");
    case "order_finalized":
      return t("OPO validé : laissé tel quel. Rouvrez-le pour qu'il suive l'ordre de l'échelon supérieur.");
    case "order_unavailable":
      return notice.message ? t("Ordre non rédigé : {message}", { message: tServer(notice.message) }) : t("Ordre non rédigé.");
    case "frame_partial":
      return t("Paragraphes à revoir (l'ordre de l'échelon supérieur ne les donne pas ou ne leur correspond pas) : {fields}.", { fields: fieldNames(notice.fields, level) });
    case "parent_missing":
      return t("Dossier de la brigade non créé : l'ordre de ses GTIA attend le sien.");
    case "not_transmissible":
      return t("Cette unité ne peut pas recevoir de dossier.");
    case "child_not_editable":
      return t("Dossier existant que vous ne pouvez pas modifier : laissé tel quel.");
    case "objective_outside": {
      const where = (notice.phases ?? [])
        .map((phase, i) => (notice.places?.[i] ? `${phase} (${notice.places[i]})` : phase))
        .join(", ");
      const text = t("Position reçue hors du fuseau de l'unité : {where}. Vérifiez ses limites ou l'objectif dans l'ordre du chef.", { where });
      return notice.parent?.length
        ? `${text} ${t("Elle est aussi hors du fuseau du chef ({phases}) : à reprendre dans son ordre.", { phases: notice.parent.join(", ") })}`
        : text;
    }
    case "objective_placed":
      return t("Compagnies posées sur l'objectif reçu en {phase}, avec les tâches que le chef donne à l'unité.", { phase: notice.phase ?? "T3" });
    case "mission_reserve":
      return t("Réserve du chef : compagnies posées dans sa zone d'attente, mission « {mission} » (son emploi). Idée de manœuvre et missions des compagnies à revoir.", { mission: missionName(notice.mission ?? "") });
    case "eni_template":
      return t("Aucun ennemi de la carte du chef dans le fuseau de l'unité : ennemi type de sa mission gardé, à vérifier.");
    case "map_kept":
      return t("Carte retouchée dans le dossier gardée : les positions et missions reçues du chef n'y sont pas reposées.");
    case "removed":
      return t("Unité retirée de l'ordre du chef : son dossier le signale, il n'est plus mis à jour ni repris dans le document regroupé.");
    case "weather_kept":
      return t("Météo choisie dans le dossier gardée : elle diffère de celle de l'ordre du chef.");
    case "time_zone_kept":
      return t("Fuseau horaire choisi dans le dossier gardé : il diffère de celui de l'ordre du chef.");
    case "weather_settled":
      // Avis de la météo ramenée au terrain du subordonné (`weather_settle`), phrase du serveur.
      return notice.message ? tServer(notice.message) : "";
    default:
      return "";
  }
}

/** Action attendue de la vue, désignée par un seul bouton qui pulse (R5) : générer (jamais fait), reprendre
 *  (interrompue et non reprise d'office), mettre à jour (ordre du chef changé, subordonné sans ordre, échec),
 *  aucune sinon. */
export type NextAction = "generate" | "resume" | "update" | null;

export function nextAction(state: DeclinationState | undefined, resumeFailed: boolean): NextAction {
  if (!state || !state.available) return null;
  const run = state.declination;
  // Subordonnés qui ont déjà leur dossier (reçu de l'échelon au-dessus, registre TD-8) : une mise à jour n'est
  // utile que si l'ordre a changé depuis ou qu'un subordonné n'a pas encore le sien.
  if (!run && state.existing) return state.existing.outdated || state.existing.new_items.length ? "update" : null;
  if (!run) return "generate";
  if (run.status === "interrupted") return resumeFailed ? "resume" : null;
  if (run.status === "failed") return "update";
  if (run.status === "done" && (run.outdated || Boolean(run.new_items?.length))) return "update";
  return null;
}

/** Libellé du bouton principal selon l'état (lancement, reprise, remise à jour). */
export function primaryKind(state: DeclinationState | undefined): "generate" | "update" | "resume" {
  const run = state?.declination;
  if (!run) return state?.existing ? "update" : "generate";
  if (run.status === "interrupted") return "resume";
  return "update";
}

/** Subordonnés qui ont déjà leur dossier sans que ce dossier les ait déclinés (registre TD-8) : « 6 ordres
 *  existants, à jour », « … à mettre à jour », « 1 ordre à rédiger » pour ceux qui n'en ont pas. */
export function existingText(existing: { count: number; outdated: boolean; new_items: string[] }): string {
  const { count, outdated, new_items: missing } = existing;
  const head = outdated
    ? count <= 1 ? t("{n} ordre existant, à mettre à jour", { n: count }) : t("{n} ordres existants, à mettre à jour", { n: count })
    : count <= 1 ? t("{n} ordre existant, à jour", { n: count }) : t("{n} ordres existants, à jour", { n: count });
  return missing.length ? `${head} · ${plannedText(missing.length)}` : head;
}

/** Bandeau d'un dossier subordonné dont l'ordre du chef a changé depuis sa déclinaison (audit du 10/10/2026) : texte
 *  et action attendue — `update` (relancer la déclinaison depuis le dossier du chef), `open` (dossier du chef qu'on
 *  ne peut pas modifier : l'ouvrir), aucune (dossier du chef non partagé). null : rien à signaler. */
export function receivedNotice(
  received: DeclinationReceived | null | undefined,
): { text: string; action: "update" | "open" | "removed" | null } | null {
  const removed = received?.removed;
  if (removed) {
    if (removed.kept) return null;
    const unit = removed.unit ? t("« {unit} »", { unit: removed.unit }) : t("Cette unité");
    const head = removed.issuer
      ? t("{unit} ne figure plus dans l'ordre de « {chef} » : ce dossier n'en reçoit plus rien.", { unit, chef: removed.issuer })
      : t("{unit} ne figure plus dans l'ordre de l'échelon supérieur : ce dossier n'en reçoit plus rien.", { unit });
    return {
      text: `${head} ${t("Détachez-le pour en faire un dossier indépendant, ou gardez-le tel quel ; son fuseau peut chevaucher celui d'une autre unité.")}`,
      action: "removed",
    };
  }
  if (!received?.outdated) return null;
  const head = received.changed
    ? t("L'ordre de « {name} » a changé depuis la rédaction de cet ordre.", { name: received.changed })
    : t("L'ordre de l'échelon supérieur a changé depuis la rédaction de cet ordre.");
  const from = received.from;
  if (from?.editable)
    return {
      text: `${head} ${t("Mettez à jour les ordres subordonnés pour qu'il le suive ; ce que vous y avez modifié vous-même est gardé.")}`,
      action: "update",
    };
  if (from)
    return {
      text: `${head} ${t("Les ordres subordonnés se mettent à jour depuis le dossier « {name} », par un compte qui peut le modifier.", { name: from.name })}`,
      action: "open",
    };
  return {
    text: `${head} ${t("Les ordres subordonnés se mettent à jour depuis le dossier de l'échelon supérieur, qui n'est pas partagé avec vous.")}`,
    action: null,
  };
}

/** Dossiers de la déclinaison rangés par brigade (division) : les brigades, chacune suivie de ses GTIA. */
export function itemTree(items: readonly DeclinationItem[]): { item: DeclinationItem; children: DeclinationItem[] }[] {
  const roots = items.filter((i) => i.parent === null);
  return roots.map((item) => ({ item, children: items.filter((i) => i.parent === item.key) }));
}

/** Section du document regroupé : parties communes d'un groupe, ou partie d'un subordonné. */
export interface GroupedSection {
  id: string;
  title: string;
  kind: "common" | "unit";
  /** Profondeur dans le sommaire (division : 2 sous « Partie B — B.1 … »). */
  depth: number;
  exerciseId: string;
  level: Level;
  label: string;
  /** S/GTIA : « Compagnie » ou « Escadron » (badge). */
  unitLabel: string | null;
  review: string[];
  nodes: GroupedNode[];
}

/** Entrée du sommaire : titre de partie (non sélectionnable) ou section. */
export type IndexEntry = { type: "label"; text: string; depth: number } | { type: "section"; section: GroupedSection };

/** Sommaire et sections du document regroupé, d'après la suite de ses éléments (intertitres de niveau 1 : parties
 *  d'une division ; de niveau 2 : parties communes, partie d'un subordonné, ou groupe des GTIA d'une brigade). */
export function groupedSections(doc: GroupedDocument | undefined): { index: IndexEntry[]; sections: GroupedSection[] } {
  const index: IndexEntry[] = [];
  const sections: GroupedSection[] = [];
  if (!doc) return { index, sections };
  let current: GroupedSection | null = null;
  let nested = false; // sous un groupe « B.n — GTIA de la … »
  for (const node of doc.nodes) {
    if (node.kind === "heading" && node.level === 1) {
      current = null;
      nested = false;
      index.push({ type: "label", text: node.text, depth: 0 });
      continue;
    }
    if (node.kind === "heading" && node.level === 2) {
      const unit = node.meta?.unit;
      const common = node.meta?.common;
      if (!unit && !common) {
        current = null;
        nested = true;
        index.push({ type: "label", text: node.text, depth: 1 });
        continue;
      }
      current = {
        id: node.id ?? `s${sections.length}`,
        title: node.text,
        kind: unit ? "unit" : "common",
        depth: nested ? 2 : 1,
        exerciseId: (unit ?? common)!.exercise_id,
        level: (unit ?? common)!.level,
        label: unit?.label ?? "",
        unitLabel: unit?.unit_label ?? null,
        review: unit?.review ?? [],
        nodes: [],
      };
      sections.push(current);
      index.push({ type: "section", section: current });
      continue;
    }
    current?.nodes.push(node);
  }
  return { index, sections };
}

/** Après un import : la déclinaison choisie dans la fenêtre « Importer un OPO » est lancée aussitôt (08/10/2026) ;
 *  rend vrai si elle a démarré (la vue de la déclinaison suit sa progression). */
export async function startAfterImport(exerciseId: string, language: "fr" | "en"): Promise<boolean> {
  try {
    await api.startDeclination(exerciseId, language);
    return true;
  } catch {
    return false;
  }
}
